/**
 * 认证状态 (Alpine store): 登录态、当前用户、会员状态.
 *
 * 未登录只能看登录/注册页; 登录后首页与资源列表都可见,
 * 「免费资源」对所有登录用户开放, 其余资源需要会员在有效期内。
 * 资源内容由后端二次校验(403 membership_required), 前端只是体验层。
 */

import { ApiError, api, getToken, setToken } from '../lib/api'
import { avatarClass } from '../lib/avatar'
import { memberBadgeClass } from '../lib/badges'
import { formatDate } from '../lib/format'
import { normalizeLanguage } from '../lib/content-language'
import type { ContentLanguage, User } from '../types/api'
import { AUTH_ROUTES, goTo, type RouteName } from './router'

export interface AuthStore {
  user: User | null
  ready: boolean
  pending: boolean
  error: string
  isAuthenticated: boolean
  isMember: boolean
  isAdmin: boolean
  displayName: string
  memberUntilLabel: string
  memberExpiringSoon: boolean
  /** 内容语言 (未登录时按默认双语) */
  language: ContentLanguage
  bootstrap(): Promise<void>
  /** 未登录(含令牌过期)却停留在需登录页面时, 回到登录页 */
  guard(route: RouteName): void
  login(email: string, password: string): Promise<boolean>
  /** 注册是邀请制: inviteCode 必填, 不区分大小写 */
  register(email: string, inviteCode: string, password: string): Promise<boolean>
  redeem(code: string): Promise<boolean>
  updateProfile(payload: { nickname?: string; language?: string }): Promise<boolean>
  changePassword(currentPassword: string, newPassword: string): Promise<boolean>
  logout(): void
  clearError(): void
  /** 头像标识 → 主题语义色类名 (头像底色跟随主题) */
  avatarClass(name: string | undefined): string
  /** 会员状态标签配色 (跟随主题) */
  memberBadgeClass(): string
  /** 内部辅助: 统一处理 pending / 错误信息 / 令牌失效 */
  run(action: () => Promise<void>): Promise<boolean>
}

export function authStore(): AuthStore {
  return {
    user: null,
    ready: false,
    pending: false,
    error: '',

    get isAuthenticated(): boolean {
      return this.user !== null
    },

    get isMember(): boolean {
      return this.user?.is_member === true
    },

    get isAdmin(): boolean {
      return this.user?.role === 'admin'
    },

    get displayName(): string {
      if (!this.user) return ''
      return this.user.nickname || this.user.email.split('@')[0] || this.user.email
    },

    /** 有效会员的到期日期, 与我的页面共用. */
    get memberUntilLabel(): string {
      return this.isMember ? formatDate(this.user?.member_until) : ''
    },

    /** 少于 30 天时提示续期, 无有效日期时不显示告警. */
    get memberExpiringSoon(): boolean {
      if (!this.isMember || !this.user?.member_until) return false
      const remaining = new Date(this.user.member_until).getTime() - Date.now()
      return remaining > 0 && remaining < 30 * 24 * 60 * 60 * 1000
    },

    get language(): ContentLanguage {
      return normalizeLanguage(this.user?.language)
    },

    /** 应用启动时用已保存的令牌恢复登录态 */
    async bootstrap(): Promise<void> {
      if (!getToken()) {
        this.ready = true
        return
      }
      try {
        this.user = await api.auth.me()
      } catch {
        setToken(null)
        this.user = null
      }
      this.ready = true
    },

    /**
     * 路由守卫: 恢复登录态后若未登录, 遇到需登录的路由 (AUTH_ROUTES) 就回登录页。
     * 由 main.ts 的 x-effect 绑定路由与登录态驱动, 兼顾页面刷新与令牌中途失效。
     */
    guard(route: RouteName): void {
      if (!this.ready || this.isAuthenticated) return
      if (AUTH_ROUTES.includes(route)) {
        goTo('login')
      }
    },

    async login(email, password) {
      return this.run(async () => {
        const response = await api.auth.login(email.trim(), password)
        setToken(response.access_token)
        this.user = response.user
        goTo('home')
      })
    },

    async register(email, inviteCode, password) {
      return this.run(async () => {
        const response = await api.auth.register(
          email.trim(),
          inviteCode.trim().toUpperCase(),
          password,
        )
        setToken(response.access_token)
        this.user = response.user
        goTo('home')
      })
    },

    async redeem(code) {
      return this.run(async () => {
        this.user = await api.membership.redeem(code.trim().toUpperCase())
      })
    },

    async updateProfile(payload) {
      return this.run(async () => {
        this.user = await api.users.updateProfile(payload)
      })
    },

    async changePassword(currentPassword, newPassword) {
      return this.run(async () => {
        await api.users.changePassword(currentPassword, newPassword)
      })
    },

    logout(): void {
      setToken(null)
      this.user = null
      this.error = ''
      goTo('login')
    },

    clearError(): void {
      this.error = ''
    },

    avatarClass(name) {
      return avatarClass(name)
    },

    memberBadgeClass() {
      return memberBadgeClass(this.isMember)
    },

    /** 统一处理 pending / 错误信息 / 跳转 */
    async run(action: () => Promise<void>): Promise<boolean> {
      if (this.pending) return false
      this.pending = true
      this.error = ''
      try {
        await action()
        return true
      } catch (error) {
        if (error instanceof ApiError) {
          this.error = error.message
        } else {
          this.error = '网络异常, 请稍后重试'
        }
        return false
      } finally {
        this.pending = false
      }
    },
  }
}

/** 供 TS 页面组件调用: 取 Alpine 上的响应式 store 实例 */
export function authRef(): AuthStore | undefined {
  return window.Alpine?.store('auth') as AuthStore | undefined
}
